import * as React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Button, Stack, Stepper, Step, StepLabel, StepConnector, stepConnectorClasses, TextField, useMediaQuery, Box, FormControl, useTheme, FormControlLabel, Typography, FormGroup, Checkbox, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../../../components/Header';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { editLiasoning } from '../../../redux/slice/liasoning.slice';
import { Image_URL } from '../../../utils/baseURL';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DownloadIcon from '@mui/icons-material/Download';


const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage: 'none',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage: 'linear-gradient( 95deg,#134670 0%, #2B89D5 120%)',
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
        borderRadius: 1,
    },
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        left: 'calc(-50% + 20px)',
        right: 'calc(50% + 20px)',
    },
    '@media (max-width: 425px)': {
        [`&.${stepConnectorClasses.alternativeLabel}`]: {
            top: 15,
            left: 'calc(-50% + 15px)',
            right: 'calc(50% + 15px)',
        },
    },
    '@media (min-width: 426px)': {
        [`&.${stepConnectorClasses.alternativeLabel}`]: {
            top: 25,
            left: 'calc(-50% + 25px)',
            right: 'calc(50% + 25px)',
        },
    },

}));

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState, isVerySmallScreen }) => ({
    backgroundColor: ownerState.completed ? '#134670' : ownerState.filled ? '#134670' : '#ccc',
    zIndex: 1,
    color: (ownerState.completed || ownerState.filled) ? '#fff' : '#000',
    width: isVerySmallScreen ? 30 : 50,
    height: isVerySmallScreen ? 30 : 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    border: ownerState.active ? '2px solid #008cff' : '',
    ...(ownerState.active && {
        boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
    }),
}));

function ColorlibStepIcon(props) {
    const { active, completed, filled, className, icon } = props;
    const isVerySmallScreen = useMediaQuery('(max-width:425px)');

    return (
        <ColorlibStepIconRoot
            ownerState={{ completed, filled, active }}
            className={className}
            isVerySmallScreen={isVerySmallScreen}
        >
            <Typography variant={isVerySmallScreen ? "body2" : "h6"} style={{ fontWeight: 'bold', color: completed ? '#fff' : '#000' }}>
                {icon}
            </Typography>
        </ColorlibStepIconRoot>
    );
}

ColorlibStepIcon.propTypes = {
    active: PropTypes.bool,
    className: PropTypes.string,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    filledSteps: PropTypes.array,
};

const steps = ['Application Submitted', 'FQ Status', 'FQ Paid', 'Site Details', 'Net Meter Document Upload', 'Net Meter Document Qurier', 'Net Meter Install', 'Subcidy Claim Process', 'Subcidy Received Status'];



const baseSchema = {
    fileNo: Yup.string().required('Application No is required'),
    FQPayment: Yup.string().required('FQ Payment is required'),
    AmountL: Yup.string().required('FQ Paid is required'),
    SerialNumber: Yup.string().required('Serial Number is required')
};

const checkBoxSchema = (checkBoxField, queryField, message) => ({
    [checkBoxField]: Yup.boolean().default(false),
    [checkBoxField + 'date']: Yup.string().when(checkBoxField, (val) => 
      val ? Yup.string().required(`Date is required for ${message}`) : Yup.string().nullable()
    ),
    [queryField]: Yup.string().when(checkBoxField, (val) => 
      !val ? Yup.string().required(`${message} for query entry.`) : Yup.string().nullable()
    ),
  });

const StepOneSchema = Yup.object({
    fileNo: baseSchema.fileNo,
});

const StepTwoSchema = Yup.object({
    fileNo: baseSchema.fileNo,
    FQPayment: baseSchema.FQPayment,
});

const StepThreeSchema = Yup.object({
    fileNo: baseSchema.fileNo,
    FQPayment: baseSchema.FQPayment,
    AmountL: baseSchema.AmountL
});

const StepFourSchema = Yup.object({
    ...baseSchema,
});

const StepFiveSchema = Yup.object({
    ...baseSchema,
    ...checkBoxSchema('CheckBox1', 'Query1', 'Net Meter Document Upload'),
});

const StepSixSchema = Yup.object({
    ...baseSchema,
    ...checkBoxSchema('CheckBox1', 'Query1', 'Net Meter Document Upload'),
    ...checkBoxSchema('CheckBox2', 'Query2', 'Net Meter Document Courier'),
});

const StepSevenSchema = Yup.object({
    ...baseSchema,
    ...checkBoxSchema('CheckBox1', 'Query1', 'Net Meter Document Upload'),
    ...checkBoxSchema('CheckBox2', 'Query2', 'Net Meter Document Courier'),
    ...checkBoxSchema('CheckBox3', 'Query3', 'Net Meter Install'),
});

const StepEightSchema = Yup.object({
    ...baseSchema,
    ...checkBoxSchema('CheckBox1', 'Query1', 'Net Meter Document Upload'),
    ...checkBoxSchema('CheckBox2', 'Query2', 'Net Meter Document Courier'),
    ...checkBoxSchema('CheckBox3', 'Query3', 'Net Meter Install'),
    ...checkBoxSchema('CheckBox4', 'Query4', 'Subsidy Claim Process'),
});

const StepNineSchema = Yup.object({
    ...baseSchema,
    ...checkBoxSchema('CheckBox1', 'Query1', 'Net Meter Document Upload'),
    ...checkBoxSchema('CheckBox2', 'Query2', 'Net Meter Document Courier'),
    ...checkBoxSchema('CheckBox3', 'Query3', 'Net Meter Install'),
    ...checkBoxSchema('CheckBox4', 'Query4', 'Subsidy Claim Process'),
    ...checkBoxSchema('CheckBox5', 'Query5', 'Subsidy Received Status'),
});


const stepFieldMappings = {
    0: ['fileNo'],
    1: ['FQPayment'],
    2: ['AmountL'],
    3: ['SerialNumber'],
    4: ['CheckBox1'],
    5: ['CheckBox2'],
    6: ['CheckBox3'],
    7: ['CheckBox4'],
    8: ['CheckBox5'],
};

const validationSchemas = [StepOneSchema, StepTwoSchema, StepThreeSchema, StepFourSchema, StepFiveSchema, StepSixSchema, StepSevenSchema, StepEightSchema, StepNineSchema];

const initialValues = {
    fileNo: '',
    FQPayment: '',
    AmountL: '',
    SerialNumber: '',
    // addmorephotos: [{
    //     SitePhoto: '',
    //     OtherPhoto: '',
    // }],
    CheckBox1: false,
    CheckBox1date: '',
    CheckBox2: false,
    CheckBox2date: '',
    CheckBox3: false,
    CheckBox3date: '',
    CheckBox4: false,
    CheckBox4date: '',
    CheckBox5: false,
    CheckBox5date: '',
    applicationStatus: '',
    Query1: '',
    Query2: '',
    Query3: '',
    Query4: '',
    Query5: '',
    User_id: '',
};



export default function Addliasoning() {
    const [stepsWithErrors, setStepsWithErrors] = React.useState([]);
    const [touchedSteps, setTouchedSteps] = React.useState([false, false, false]);
    const [filterData, setFilterData] = React.useState([]);
    const [dealer, setDealar] = React.useState([]);
    const downloadRef = React.useRef(null);

    const dispatch = useDispatch();
    const { state } = useLocation();
    const liasoningData = state;
    const [filledSteps, setFilledSteps] = useState(liasoningData?.filledSteps || []);
    const isMobile = useMediaQuery('(max-width:1024px)');
    const isMobileee = useMediaQuery('(max-width:500px)');

    const MobileStepper = ({ steps, activeStep, completedSteps, filledSteps }) => {
        const isVerySmallScreen = useMediaQuery('(max-width:425px)');
        const isMediumScreen = useMediaQuery('(min-width:426px) and (max-width:1024px)');
        const theme = useTheme();


        const renderStep = (label, index) => (
            <Step key={label} completed={liasoningData?.filledSteps?.includes(index)}>
                <StepLabel
                    StepIconComponent={ColorlibStepIcon}
                    icon={index + 1}
                    StepIconProps={{
                        active: index === activeStep,
                        completed: completedSteps.includes(index),
                        filled: filledSteps.includes(index)
                    }}
                >

                    <Typography style={{
                        color: liasoningData?.filledSteps?.includes(index) ? '#6da2cd' :
                            index === activeStep ? theme.palette.mode === 'dark' ? '#fff' : "#000" :
                                filledSteps.includes(index) ? '#6da2cd' : " ",
                        fontWeight: '600',
                        fontSize: isVerySmallScreen ? '10px' : '12px'
                    }}>
                        {label}
                    </Typography>
                </StepLabel>
            </Step>
        );

        if (isVerySmallScreen) {
            const groupedSteps = [];
            for (let i = 0; i < steps.length; i += 3) {
                groupedSteps.push(steps.slice(i, i + 3));
            }

            return (
                <Box>
                    {groupedSteps.map((group, groupIndex) => (
                        <React.Fragment key={groupIndex}>
                            <Stepper
                                alternativeLabel
                                activeStep={activeStep - groupIndex * 3}
                                connector={<ColorlibConnector />}
                                sx={{
                                    '& .MuiStepLabel-root': { padding: '0 4px' },
                                    '& .MuiStepConnector-line': { top: 15 },
                                    '& .MuiStepConnector-root': {
                                        left: 'calc(-50% + 15px)',
                                        right: 'calc(50% + 15px)',
                                    },
                                    '& .MuiStep-root:first-of-type .MuiStepConnector-root': {
                                        left: '50%',
                                    },
                                    '& .MuiStep-root:last-child .MuiStepConnector-root': {
                                        right: '50%',
                                    },
                                }}
                            >
                                {group.map((label, index) => renderStep(label, groupIndex * 3 + index))}
                            </Stepper>
                            {groupIndex < groupedSteps.length - 1 && <Box sx={{ my: 2 }} />}
                        </React.Fragment>
                    ))}
                </Box>
            );
        } else if (isMediumScreen) {
            const firstRowSteps = steps.slice(0, 5);
            const secondRowSteps = steps.slice(5);

            return (
                <Box>
                    <Stepper alternativeLabel activeStep={activeStep > 4 ? 4 : activeStep} connector={<ColorlibConnector />}>
                        {firstRowSteps.map((label, index) => renderStep(label, index))}
                    </Stepper>
                    <Box sx={{ my: 1 }} />
                    <Stepper alternativeLabel activeStep={activeStep > 5 ? activeStep - 5 : 0} connector={<ColorlibConnector />}>
                        {secondRowSteps.map((label, index) => renderStep(label, index + 5))}
                    </Stepper>
                </Box>
            );
        } else {
            return (
                <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
                    {steps.map((label, index) => renderStep(label, index))}
                </Stepper>
            );
        }
    };

    const location = useLocation();
    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState(liasoningData?.filledSteps?.map((v) => parseInt(v)) || []);
    const [applicationStatus] = useState([
        'APPLICATION SUBMITTED',
        'FQ STATUS',
        'FQ PAID',
        'SITE DETAILS',
        'NET METER DOCUMENT UPLOAD',
        'NET METER DOCUMENT QUERIER',
        'NET METER INSTALL',
        'SUBSIDY CLAIM PROCESS',
        'SUBSIDY RECEIVED STATUS'
    ])

    useEffect(() => {
        if (location.state) {
            if (location.state.completedSteps) {
                setActiveStep(location.state.completedSteps);
            }
        }
    }, [location.state]);

    const Electrician = useSelector(state => state?.electrician?.Electrician);
    const electricianField = Array.isArray(liasoningData?.electricianData)
        ? liasoningData.electricianData.find(e => e.liasoningId === liasoningData._id)?.MultipleImages || ''
        : '';

    console.log("electricianField", electricianField);


    useEffect(() => {
        setValues(liasoningData);
        setFilledSteps(liasoningData?.filledSteps);

    }, [dispatch, liasoningData?._id]);


    const isLastStep = activeStep === steps.length - 1;

    const isStepFilled = (step) => {
        const stepFields = {
            0: ['fileNo', 'fileDate'],
            1: ['FQPayment'],
            2: ['AmountL', 'AmountDate'],
            3: ['SerialNumber', 'SerialNumberDate'],
            4: ['CheckBox1', 'CheckBox1date'],
            5: ['CheckBox2', 'CheckBox2date'],
            6: ['CheckBox3', 'CheckBox3date'],
            7: ['CheckBox4', 'CheckBox4date'],
            8: ['CheckBox5', 'CheckBox5date'],
        };



        if (!stepFields[step]) return false;
        return stepFields[step].every(field => values[field]);
    };

    console.log("location.state", location.state?.filledSteps);

    // const handleNext = (values, actions) => {
    //     setTouchedSteps((prevTouchedSteps) => {
    //         const newTouchedSteps = [...prevTouchedSteps];
    //         newTouchedSteps[activeStep] = true;
    //         return newTouchedSteps;
    //     });
    //     if (activeStep >= 3) {
    //         const firstFourStepsFilled = [0, 1, 2, 3].every(s => isStepFilled(s));
    //         if (!firstFourStepsFilled) {
    //             alert("Please fill in the first four steps before proceeding.");
    //             return;
    //         }
    //     }
    //     if (isStepFilled(activeStep)) {
    //         setFilledSteps(prev => [...new Set([...prev, activeStep.toString()])]);
    //         setCompletedSteps(prev => [...new Set([...prev, activeStep])]);
    //     }

    //     if (!isLastStep) {
    //         setActiveStep((prevActiveStep) => prevActiveStep + 1);
    //     } else {
    //         setActiveStep(steps.length - 1);
    //         actions.validateForm().then((errors) => {
    //             const user_id = localStorage.getItem('id');
    //             if (Object.keys(errors).length === 0) {
    //                 let serialNumberValue;
    //                 if (Array.isArray(electricianField)) {
    //                     serialNumberValue = electricianField.map(item => item.electricianField).join(', ');
    //                 } else {
    //                     serialNumberValue = electricianField || values.SerialNumber;
    //                 }
    //                 const submissionValues = {
    //                     ...values,
    //                     applicationStatus: applicationStatus[activeStep],
    //                     filledSteps: [...filledSteps, activeStep].map(String),
    //                     User_id: user_id,
    //                     SerialNumber: serialNumberValue
    //                 };
    //                 if (liasoningData?._id) {
    //                     delete submissionValues.isActive;
    //                     delete submissionValues.createdAt;
    //                     delete submissionValues.updatedAt;
    //                     dispatch(editLiasoning({ id: liasoningData?._id, ...submissionValues }));
    //                 }
    //                 actions.resetForm();
    //                 setActiveStep(0);
    //                 setValues(initialValues);
    //                 navigate('/admin/liasoning');
    //             } else {
    //                 console.log("Form validation errors:", errors);
    //                 setStepsWithErrors((prevStepsWithErrors) => {
    //                     const newStepsWithErrors = [...prevStepsWithErrors];
    //                     newStepsWithErrors[activeStep] = true;
    //                     return newStepsWithErrors;
    //                 });
    //                 alert('Please fix the errors in the form before submitting');
    //             }
    //             actions.setSubmitting(false);
    //             handleSubmit();
    //         });
    //     }
    // };

    // const handleNext = () => {
    //     validateForm().then((errors) => {
    //         if (Object.keys(errors).length === 0) {
    //             setFilledSteps(prev => [...new Set([...prev, activeStep.toString()])]);
    //             setCompletedSteps(prev => [...new Set([...prev, activeStep])]);

    //             if (!isLastStep) {
    //                 setActiveStep((prev) => prev + 1);
    //             } else {
    //                 handleSubmit(values);
    //             }
    //         } else {
    //             console.log("Validation Errors:", errors);
    //             alert("Please fix the errors in this step before proceeding.");
    //         }
    //     });
    // };



    const navigate = useNavigate();

    // const handleSubmit = () => {
    //     setFilledSteps(prev => [...new Set([...prev, activeStep])]);
    //     validationSchemas.validate(values, { abortEarly: false })
    //         .then(() => {
    //             const user_id = localStorage.getItem('id');

    //             let serialNumberValue;
    //             if (Array.isArray(electricianField)) {
    //                 serialNumberValue = electricianField.map(item => item.electricianField).join(', ');
    //             } else {
    //                 serialNumberValue = electricianField || values.SerialNumber;
    //             }
    //             console.log(activeStep, 'activeStep');
    //             console.log(applicationStatus[activeStep], 'applicationStatus[activeStep]');

    //             const submissionValues = {
    //                 ...values,
    //                 applicationStatus: applicationStatus[activeStep],
    //                 filledSteps: [...filledSteps, activeStep].map(String),
    //                 User_id: user_id,
    //                 SerialNumber: serialNumberValue
    //             };
    //             console.log(submissionValues, 'submissionValues');


    //             if (liasoningData?._id) {
    //                 dispatch(editLiasoning({ id: liasoningData?._id, ...submissionValues }));
    //             } else {

    //             }
    //             setActiveStep(0);
    //             navigate('/admin/liasoning');
    //         })
    //         .catch((validationErrors) => {
    //             console.log("Form validation errors:", validationErrors);
    //             alert("Please fill in all required fields before submitting.");
    //         });

    //     const currentDate = new Date().toISOString().split('T')[0];
    //     setFieldValue('fileDate', currentDate);
    // };

    // const handleSubmit = (values) => {
    //     validateForm().then((errors) => {
    //         if (Object.keys(errors).length === 0) {
    //             const user_id = localStorage.getItem('id');
    //             let serialNumberValue = Array.isArray(electricianField) 
    //                 ? electricianField.map(item => item.electricianField).join(', ')
    //                 : electricianField || values.SerialNumber;

    //             const submissionValues = {
    //                 ...values,
    //                 applicationStatus: applicationStatus[activeStep],
    //                 filledSteps: [...filledSteps, activeStep].map(String),
    //                 User_id: user_id,
    //                 SerialNumber: serialNumberValue
    //             };

    //             if (liasoningData?._id) {
    //                 delete submissionValues.isActive;
    //                 delete submissionValues.createdAt;
    //                 delete submissionValues.updatedAt;
    //                 dispatch(editLiasoning({ id: liasoningData?._id, ...submissionValues }));
    //             }

    //             formik.resetForm();
    //             setActiveStep(0);
    //             navigate('/admin/liasoning');
    //         } else {
    //             console.log("Form validation errors:", errors);
    //             alert("Please fill in all required fields before submitting.");
    //         }
    //     });

    //     const currentDate = new Date().toISOString().split('T')[0];
    //     setFieldValue('fileDate', currentDate);
    // };

    const handleNext = () => {
        setTouchedSteps((prevTouchedSteps) => {
            const newTouchedSteps = [...prevTouchedSteps];
            newTouchedSteps[activeStep] = true;
            return newTouchedSteps;
        });

        // Ensure first four steps are filled before proceeding beyond step 3
        if (activeStep >= 3) {
            const firstFourStepsFilled = [0, 1, 2, 3].every(s => isStepFilled(s));
            if (activeStep >= 3 && !location.state?.filledSteps?.includes('3')) {
                alert("Please save first four steps before proceeding.");
                return;
            }
        }

        // If step is filled, mark it as completed
        if (isStepFilled(activeStep)) {
            setFilledSteps(prev => [...new Set([...prev, activeStep.toString()])]);
            setCompletedSteps(prev => [...new Set([...prev, activeStep])]);
        }

        if (!isLastStep) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } else {
            handleSubmit();
        }
    };



    const getStepValues = (values, stepIndex) => {
        const stepFields = stepFieldMappings[stepIndex] || [];
        return stepFields.reduce((obj, key) => {
            if (values[key] !== undefined && values[key] !== '' && values[key] !== false) {
                obj[key] = values[key];
            }
            return obj;
        }, {});
    };

    const handleSubmit = () => {
        validateForm().then((errors) => {

            if (Object.keys(errors).length === 0) {
                const user_id = localStorage.getItem('id');

                // Ensure all steps with filled values are included
                const allFilledSteps = Object.keys(stepFieldMappings)
                    .map(Number) // Convert keys to numbers
                    .filter((index) => {
                        const stepValues = getStepValues(values, index);
                        return Object.keys(stepValues).length > 0; // Check if any field in the step is filled
                    });

                // Convert existing filledSteps to numbers before merging
                const filledStepsAsNumbers = filledSteps.map(Number);

                // Ensure only sequential steps are added (if a step is filled, previous steps must be too)
                const updatedFilledSteps = [];
                for (let i = 0; i < allFilledSteps.length; i++) {
                    if (i === 0 || updatedFilledSteps.includes(allFilledSteps[i] - 1)) {
                        updatedFilledSteps.push(allFilledSteps[i]); // Add step only if previous step exists
                    }
                }

                // Merge with already filled steps
                const finalFilledSteps = Array.from(new Set([...filledStepsAsNumbers, ...updatedFilledSteps]));

                // Find the highest step value for applicationStatus
                const maxFilledStep = Math.max(...finalFilledSteps);

                const submissionValues = {
                    ...values,
                    applicationStatus: applicationStatus[maxFilledStep], // Use highest step for status
                    filledSteps: finalFilledSteps.map(String), // Convert back to string array
                    User_id: user_id,
                };

                console.log("submissionValues", submissionValues, activeStep);

                console.log("submissionValues", submissionValues, activeStep);


                if (liasoningData?._id) {
                    console.log("{ id: liasoningData?._id, ...submissionValues }", { id: liasoningData?._id, ...submissionValues });

                    dispatch(editLiasoning({ id: liasoningData?._id, ...submissionValues }));
                }

                // Reset the form and navigate after successful submission
                formik.resetForm();
                setActiveStep(0);
                navigate('/admin/liasoning');
            } else {
                const errorMessages = Object.values(errors).join("\n");
                alert(errorMessages);

                // Mark the step as having errors
                setStepsWithErrors((prevStepsWithErrors) => {
                    const newStepsWithErrors = [...prevStepsWithErrors];
                    newStepsWithErrors[activeStep] = true;
                    return newStepsWithErrors;
                });
            }
        });

        // Set the file date
        const currentDate = new Date().toISOString().split('T')[0];
        setFieldValue('fileDate', currentDate);
    };



    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    console.log(activeStep, "activeStepactiveStep")
    const handleStep = (step) => {
        console.log(activeStep >= 3, location.state?.filledSteps?.includes('3'), activeStep, location.state?.filledSteps, "hshsh");

        const isStep1Filled = isStepFilled(0);
        if (step >= 4) {
            // const firstFourStepsFilled = [0, 1, 2, 3].every(s => isStepFilled(s));
            // if (!(activeStep >= 3 && location.state?.filledSteps?.includes('3'))) {
            //     alert("Please save first four steps before proceeding.");
            //     return;
            // }

            if (
                !['0', '1', '2', '3'].every(step => location.state?.filledSteps?.includes(step))) {
                    alert("Please save first four steps before proceeding.");
                    return;
            }
        }
        if (step >= 1 && !isStep1Filled) {
            alert("Please fill in Step 1 before proceeding to other steps.");
            return;
        }
        setActiveStep(step);
    };


    const isNonMobile = useMediaQuery("(min-width:600px)");
    const theme = useTheme();

    // const { handleBlur, handleChange, values, touched, errors, setFieldValue, setValues, validateForm, setFieldError } = useFormik({
    //     initialValues,
    //     validationSchema: validationSchemas[activeStep],
    //     // onSubmit: handleNext,
    //     onSubmit: (values, actions) => handleSubmit(values, actions)
    // });

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchemas[activeStep],
        onSubmit: (values) => handleSubmit(values) // Remove actions parameter here
    });

    const { handleBlur, handleChange, values, touched, errors, setFieldValue, setValues, validateForm, setFieldError } = formik;


    useEffect(() => {
        const currentDate = new Date().toISOString().split('T')[0];
        if (values?.fileNo) {
            setFieldValue('fileDate', liasoningData?.fileDate ? liasoningData.fileDate : currentDate)
        }
        if (values?.AmountL) {
            setFieldValue('AmountDate', liasoningData?.AmountDate ? liasoningData.AmountDate : currentDate);
        }
        if (values?.SerialNumber || electricianField) {
            setFieldValue('SerialNumberDate', liasoningData?.SerialNumberDate ? liasoningData.SerialNumberDate : currentDate);
        }
    }, [values?.fileNo, values?.AmountL, values?.SerialNumber, electricianField, setFieldValue]);

    const handleCheckBoxChange = (event) => {
        const { name, checked } = event.target;
        setFieldValue(name, checked);

        const currentDate = new Date().toISOString().split('T')[0];
        const dateFieldName = `${name}date`;

        if (checked) {
            setFieldValue(dateFieldName, liasoningData?.[dateFieldName] ? liasoningData[dateFieldName] : currentDate);
        } else {
            setFieldValue(dateFieldName, '');
        }
    };

    const [addPhotos, setAddPhotos] = useState(initialValues.addmorephotos);

    const handleAddRow = () => {
        setAddPhotos([...addPhotos, { SitePhoto: null, OtherPhoto: null }]);
    };

    const hasElectricianData = Array.isArray(electricianField) && electricianField.length > 0;

    // const handleDownload = async () => {
    //     if (viewImage) {
    //         try {
    //             let response;
    //             let downloadUrl;
    //             let fileExtension = '';
    //             if (viewImage instanceof Blob) {
    //                 downloadUrl = URL.createObjectURL(viewImage);
    //                 if (viewImage.type === 'application/pdf') {
    //                     fileExtension = 'pdf';
    //                 }
    //             } else {
    //                 if (id) {
    //                     const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(viewImage)}`;
    //                     response = await fetch(proxyUrl);
    //                 } else {
    //                     response = await fetch(viewImage);
    //                 }

    //                 if (response.ok) {
    //                     const blob = await response.blob();
    //                     downloadUrl = URL.createObjectURL(blob);
    //                     if (blob.type === 'application/pdf') {
    //                         fileExtension = 'pdf';
    //                     }
    //                 } else {
    //                     console.error('Failed to download image');
    //                     return;
    //                 }
    //             }

    //             const link = document.createElement('a');
    //             link.href = downloadUrl;
    //             link.download = `dealer${fileExtension}`;
    //             document.body.appendChild(link);
    //             link.click();
    //             document.body.removeChild(link);
    //             URL.revokeObjectURL(downloadUrl);
    //         } catch (error) {
    //             console.error('Error in download process:', error);
    //         }
    //     } else {
    //         console.error('No image to download');
    //     }
    // };

    const [openView, setOpenView] = React.useState(false);
    const [viewImage, setViewImage] = React.useState(null);

    const handleView = (imageType) => {
        let imageUrl;
        const baseUrl = Image_URL;

        console.log("values.electricianImage lis", values, values.electricianImage, `${baseUrl}/${imageType}`);

        setViewImage(`${baseUrl}/${imageType}`);
        setOpenView(true);

        // if (imageType.startsWith('MultipleImages')) {
        //     const match = imageType.match(/MultipleImages\[(\d+)\](?:\.electricianImage)?$/);
        //     if (match) {
        //         const index = parseInt(match[1], 10);
        //         if (values.electricianImage && values.electricianImage[index]) {
        //             imageUrl = values.electricianImage[index] instanceof File
        //                 ? URL.createObjectURL(values.electricianImage[index])
        //                 : `${baseUrl}/${values.electricianImage[index]}`;
        //         } else if (values.MultipleImages && values.MultipleImages[index] && values.MultipleImages[index].electricianImage) {
        //             imageUrl = `${baseUrl}/${values.MultipleImages[index].electricianImage}`;
        //         } else if (values.MultipleImages && values.MultipleImages[index] && values.MultipleImages[index].UploadPaymentSlip) {
        //             imageUrl = `${baseUrl}/${values.MultipleImages[index].UploadPaymentSlip}`;
        //         }
        //     }
        // } else if (imageType.startsWith('MultipleDetails')) {
        //     const match = imageType.match(/MultipleDetails\[(\d+)\](?:\.electricianImage)?$/);
        //     if (match) {
        //         const index = parseInt(match[1], 10);
        //         if (values.electricianImage && values.electricianImage[index]) {
        //             imageUrl = values.electricianImage[index] instanceof File
        //                 ? URL.createObjectURL(values.electricianImage[index])
        //                 : `${baseUrl}/${values.electricianImage[index]}`;
        //         } else if (values.MultipleImages && values.MultipleImages[index] && values.MultipleImages[index].electricianImage) {
        //             imageUrl = `${baseUrl}/${values.MultipleImages[index].electricianImage}`;
        //         }
        //     }
        // }

        // if (imageUrl) {
        //     setViewImage(imageUrl);
        //     setOpenView(true);
        // } else {
        //     console.error("No image found for the selected type:", imageType);
        // }
    };

    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));


    const handleCloseView = () => {
        setOpenView(false);
    };

    const handleDownload = async () => {
        if (viewImage) {
            try {
                let response;
                let downloadUrl;
                let fileExtension = '';
                if (viewImage instanceof Blob) {
                    downloadUrl = URL.createObjectURL(viewImage);
                    if (viewImage.type === 'application/pdf') {
                        fileExtension = 'pdf';
                    }
                } else {
                    if (liasoningData?._id) {
                        const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(viewImage)}`;
                        response = await fetch(proxyUrl);
                    } else {
                        response = await fetch(viewImage);
                    }

                    if (response.ok) {
                        const blob = await response.blob();
                        downloadUrl = URL.createObjectURL(blob);
                        if (blob.type === 'application/pdf') {
                            fileExtension = 'pdf';
                        }
                    } else {
                        console.error('Failed to download image');
                        return;
                    }
                }

                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `dealer${fileExtension}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                console.error('Error in download process:', error);
            }
        } else {
            console.error('No image to download');
        }
    };

    console.log(location.state?.filledSteps.some((v) => parseInt(v) <= 3), activeStep, location.state?.filledSteps, "location.state?.filledSteps");


    return (
        <Box m="2px" sx={{ overflow: isMobileee ? 'auto' : 'hidden', }}>
            {liasoningData?._id ? (
                <Header
                    title="UPDATE LIASONING"
                    subtitle="Update Your Liasoning Here"
                />
            ) : (
                <Header
                    title="ADD LIASONING"
                    subtitle="Manage Your Liasoning Here"
                />
            )}
            <form onSubmit={handleSubmit}>
                <Stack sx={{ width: '100%', marginTop: '30px', backgroundcolor: "#ff6b39" }} spacing={4}>
                    {isMobile ? (
                        <MobileStepper
                            steps={steps}
                            activeStep={activeStep}
                            completedSteps={completedSteps}
                            filledSteps={filledSteps}
                        />
                    ) : (
                        <Stepper activeStep={activeStep} alternativeLabel connector={<ColorlibConnector />}>
                            {steps.map((label, index) => (
                                <Step
                                    key={label}
                                    completed={completedSteps.includes(index)}
                                    onClick={() => handleStep(index)}
                                // completed={
                                //     liasoningData?.filledSteps?.includes(index) || (
                                //         liasoningData?.filledSteps
                                //             .some(filledIndex => filledIndex >= index)
                                //     )
                                // }
                                >
                                    <StepLabel
                                        StepIconComponent={ColorlibStepIcon}
                                        StepIconProps={{
                                            filled: filledSteps?.includes(index),
                                            className: 'stepper-indicator'
                                        }}
                                    >
                                        <Typography style={{
                                            color: liasoningData?.filledSteps?.includes(index)  ? '#6da2cd' :
                                                filledSteps?.includes(index) ? '#ff6b39' : theme.palette.text.primary,
                                            fontWeight: '600'
                                        }}>
                                            {label}
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    )}

                    {activeStep === 0 && (
                        <>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                }}
                            >
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120, width: { xs: 250, sm: 'auto' } }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        name="fileNo"
                                        label="Application No :"
                                        value={values?.fileNo}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!touched.fileNo && !!errors.fileNo}
                                        helperText={touched.fileNo && errors.fileNo}
                                        sx={{
                                            gridColumn: 'span 2',
                                        }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120, width: { xs: 250, sm: 'auto' } }}>
                                    <TextField
                                        disabled
                                        id="fileDate"
                                        label="Date :"
                                        type="date"
                                        variant="filled"
                                        value={values?.fileDate}
                                        name="fileDate"
                                        InputLabelProps={{
                                            shrink: true,
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Box>
                        </>
                    )}
                    {activeStep === 1 && (
                        <>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                }}
                            >
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120, width: { xs: 250, sm: "auto" } }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        name="FQPayment"
                                        label="FQ Payment :"
                                        value={values?.FQPayment}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!touched?.FQPayment && !!errors?.FQPayment}
                                        helperText={touched?.FQPayment && errors?.FQPayment}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Box>
                        </>
                    )}
                    {activeStep === 2 && (
                        <>
                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                }}
                            >
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120, width: { xs: 250, sm: "auto" } }}>
                                    <TextField
                                        fullWidth
                                        variant="filled"
                                        type="text"
                                        name="AmountL"
                                        label="Amount :"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values?.AmountL}
                                        error={!!touched?.AmountL && !!errors?.AmountL}
                                        helperText={touched?.AmountL && errors?.AmountL}
                                        sx={{ gridColumn: "span 2" }}
                                        InputLabelProps={{
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                                <FormControl variant="filled" sx={{ m: 1, minWidth: 120, width: { xs: 250, sm: "auto" } }}>
                                    <TextField
                                        disabled
                                        id="AmountDate"
                                        label="Date :"
                                        type="date"
                                        variant="filled"
                                        value={values?.AmountDate}
                                        name="AmountDate"
                                        InputLabelProps={{
                                            shrink: true,
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}

                                    />
                                </FormControl>
                            </Box>
                        </>
                    )}
                    {activeStep === 3 && (
                        <>

                            <Box
                                display="grid"
                                gap="30px"
                                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                }}
                            >

                                <FormControl
                                    variant="filled"
                                    sx={{ m: 1, minWidth: 120, width: { xs: 250, sm: "auto" } }}
                                >
                                    {hasElectricianData ? (
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '20px'
                                        }}>
                                            {electricianField.map((item, index) => (
                                                <Box
                                                    key={item._id || index}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        gap: '10px',
                                                        marginLeft: "50px"
                                                    }}
                                                >
                                                    {
                                                        item.electricianImage && (typeof item.electricianImage === 'string' && item.electricianImage.toLowerCase().endsWith('.pdf') ? (
                                                            <iframe
                                                                src={`${Image_URL}/${item.electricianImage}`}
                                                                style={{ width: '50px', height: '50px', border: 'none' }}
                                                                title="PDF Preview"
                                                            />
                                                        ) : (
                                                            <img
                                                                src={`${Image_URL}/${item.electricianImage}`}
                                                                alt="avatar"
                                                                width="50"
                                                                height="50"
                                                                style={{ objectFit: 'cover', borderRadius: '5px', marginLeft: '20px' }}
                                                            />
                                                        )
                                                        )
                                                    }
                                                    <IconButton aria-label="edit" onClick={() => handleView(item.electricianImage)}>
                                                        <RemoveRedEyeIcon />
                                                    </IconButton>
                                                    <TextField
                                                        id={`SerialNumber-${index}`}
                                                        fullWidth
                                                        variant="filled"
                                                        type="text"
                                                        label={index === 0 ? "Panel and inverter serial number" : `Input field ${index}`}
                                                        name={`SerialNumber-${index}`}
                                                        onChange={handleChange}
                                                        value={item.electricianField || ''}
                                                        onBlur={handleBlur}
                                                        error={!!touched?.[`SerialNumber-${index}`] && !!errors?.[`SerialNumber-${index}`]}
                                                        helperText={touched?.[`SerialNumber-${index}`] && errors?.[`SerialNumber-${index}`]}
                                                        sx={{
                                                            width: '60%'
                                                        }}
                                                        InputLabelProps={{
                                                            style: {
                                                                color: theme.palette.text.primary,
                                                            },
                                                        }}
                                                    />

                                                </Box>

                                            ))}
                                        </Box>
                                    ) : (
                                        <TextField
                                            id="SerialNumber"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            label="Panel and inverter serial number :"
                                            name="SerialNumber"
                                            onChange={handleChange}
                                            value={electricianField || values.SerialNumber}
                                            onBlur={handleBlur}
                                            error={!!touched.SerialNumber && !!errors.SerialNumber}
                                            helperText={touched.SerialNumber && errors.SerialNumber}
                                            sx={{
                                                gridColumn: 'span 2',
                                            }}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary
                                                }
                                            }}
                                        />
                                    )}
                                </FormControl>

                                <FormControl variant="filled" sx={{ m: 1, ml: 0, minWidth: 120, width: { xs: 250, sm: "auto" } }}>
                                    <TextField
                                        id="SerialNumberDate"
                                        disabled
                                        fullWidth
                                        variant="filled"
                                        type="date"
                                        label="Date :"
                                        value={values?.SerialNumberDate}
                                        name="SerialNumberDate"
                                        sx={{
                                            gridColumn: 'span 2',
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                            style: {
                                                color: theme.palette.text.primary
                                            }
                                        }}
                                    />
                                </FormControl>
                            </Box>
                        </>
                    )}
                    {activeStep === 4 && (
                        <>
                            <FormGroup>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width="50%"
                                >
                                    <FormControlLabel
                                        control={<Checkbox
                                            name="CheckBox1"
                                            checked={values?.CheckBox1}
                                            onChange={handleCheckBoxChange}
                                            sx={{
                                                color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                '&.Mui-checked': {
                                                    color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                },
                                            }}
                                        />}
                                        label="Done"
                                        sx={{ gridColumn: 'span 1' }}
                                    />
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%' }}>
                                        <TextField
                                            id="CheckBox1date"
                                            fullWidth
                                            disabled
                                            variant="filled"
                                            type="date"
                                            label="Date :"
                                            name="CheckBox1date"
                                            value={values?.CheckBox1date}
                                            InputLabelProps={{
                                                shrink: true,
                                                style: {
                                                    color: theme => theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                                <Box
                                    mt={2}
                                    display="flex"
                                    gap="30px"
                                    width="50%"
                                    gridTemplateColumns="repeat(1, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                    }}
                                >
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%', marginLeft: '75px' }}>
                                        <TextField
                                            id="Query1"
                                            fullWidth
                                            multiline
                                            rows={4}
                                            variant="filled"
                                            type="text"
                                            label="Query :"
                                            name="Query1"
                                            onChange={handleChange}
                                            value={values?.Query1}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            </FormGroup>
                        </>
                    )}
                    {activeStep === 5 && (
                        <>
                            <FormGroup>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width="50%"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="CheckBox2"
                                                checked={values?.CheckBox2}
                                                onChange={handleCheckBoxChange}
                                                sx={{
                                                    color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    '&.Mui-checked': {
                                                        color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Done"
                                        sx={{ gridColumn: 'span 1' }}
                                    />
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%' }}>
                                        <TextField
                                            id="CheckBox2date"
                                            fullWidth
                                            variant="filled"
                                            type="date"
                                            label="Date :"
                                            name="CheckBox2date"
                                            value={values?.CheckBox2date}
                                            disabled
                                            InputLabelProps={{
                                                shrink: true,
                                                style: {
                                                    color: theme => theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                                <Box
                                    mt={2}
                                    display="flex"
                                    gap="30px"
                                    width="50%"
                                    gridTemplateColumns="repeat(1, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                    }}>
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%', marginLeft: '75px' }}>
                                        <TextField
                                            id="Query2"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            multiline
                                            rows={4}
                                            label="Query :"
                                            name='Query2'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values?.Query2}
                                            error={!!touched?.Query2 && !!errors?.Query2}
                                            helperText={touched?.Query2 && errors?.Query2}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>

                            </FormGroup>
                        </>
                    )}
                    {activeStep === 6 && (
                        <>
                            <FormGroup>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width="50%"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="CheckBox3"
                                                checked={values?.CheckBox3}
                                                onChange={handleCheckBoxChange}
                                                sx={{
                                                    color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    '&.Mui-checked': {
                                                        color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Done"
                                        sx={{ gridColumn: 'span 1' }}
                                    />
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%' }}>
                                        <TextField
                                            id="CheckBox3date"
                                            fullWidth
                                            variant="filled"
                                            type="date"
                                            label="Date :"
                                            name="CheckBox3date"
                                            value={values?.CheckBox3date}
                                            disabled
                                            InputLabelProps={{
                                                shrink: true,
                                                style: {
                                                    color: theme => theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                                <Box
                                    mt={2}
                                    display="flex"
                                    gap="30px"
                                    width="50%"
                                    gridTemplateColumns="repeat(1, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                    }}>
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%', marginLeft: '75px' }}>
                                        <TextField
                                            id="Query3"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            multiline
                                            rows={4}
                                            label="Query :"
                                            name='Query3'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values?.Query3}
                                            error={!!touched?.Query3 && !!errors?.Query3}
                                            helperText={touched?.Query3 && errors?.Query3}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            </FormGroup>
                        </>
                    )}
                    {activeStep === 7 && (
                        <>
                            <FormGroup>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width="50%"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="CheckBox4"
                                                checked={values?.CheckBox4}
                                                onChange={handleCheckBoxChange}
                                                sx={{
                                                    color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    '&.Mui-checked': {
                                                        color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Done"
                                        sx={{ gridColumn: 'span 1' }}
                                    />
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%' }}>
                                        <TextField
                                            id="CheckBox4date"
                                            fullWidth
                                            variant="filled"
                                            type="date"
                                            label="Date :"
                                            name="CheckBox4date"
                                            value={values?.CheckBox4date}
                                            disabled
                                            InputLabelProps={{
                                                shrink: true,
                                                style: {
                                                    color: theme => theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                                <Box
                                    mt={2}
                                    display="flex"
                                    gap="30px"
                                    width="50%"
                                    gridTemplateColumns="repeat(1, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                    }}>
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%', marginLeft: '75px' }}>
                                        <TextField
                                            id="Query4"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            multiline
                                            rows={4}
                                            label="Query :"
                                            name='Query4'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values?.Query4}
                                            error={!!touched?.Query4 && !!errors?.Query4}
                                            helperText={touched?.Query4 && errors?.Query4}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>

                            </FormGroup>
                        </>
                    )}
                    {activeStep === 8 && (
                        <>
                            <FormGroup>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width="50%"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="CheckBox5"
                                                checked={values?.CheckBox5}
                                                onChange={handleCheckBoxChange}
                                                sx={{
                                                    color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    '&.Mui-checked': {
                                                        color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Done"
                                        sx={{ gridColumn: 'span 1' }}
                                    />
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%' }}>
                                        <TextField
                                            id="CheckBox5date"
                                            fullWidth
                                            variant="filled"
                                            type="date"
                                            label="Date :"
                                            name="CheckBox5date"
                                            value={values?.CheckBox5date}
                                            disabled
                                            InputLabelProps={{
                                                shrink: true,
                                                style: {
                                                    color: theme => theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{
                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                                <Box
                                    mt={2}
                                    display="flex"
                                    gap="30px"
                                    width="50%"
                                    gridTemplateColumns="repeat(1, minmax(0, 1fr))"
                                    sx={{
                                        "& > div": { gridColumn: isNonMobile ? undefined : "span 2" },
                                    }}>
                                    <FormControl variant="filled" sx={{ gridColumn: 'span 1', width: '100%', marginLeft: '75px' }}>
                                        <TextField
                                            id="Query5"
                                            fullWidth
                                            variant="filled"
                                            type="text"
                                            multiline
                                            rows={4}
                                            label="Query :"
                                            name='Query5'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values?.Query5}
                                            error={!!touched?.Query5 && !!errors?.Query5}
                                            helperText={touched?.Query5 && errors?.Query5}
                                            InputLabelProps={{
                                                style: {
                                                    color: theme.palette.text.primary,
                                                },
                                            }}
                                            sx={{

                                                '@media (max-width: 425px)': {
                                                    width: '200px',
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            </FormGroup>
                        </>
                    )}

                    <FormControl >
                        <Stack direction="row" spacing={2} sx={{ m: 1, minWidth: 120 }}>
                            <Button
                                variant="contained"
                                disabled={activeStep === 0}
                                onClick={handleBack}
                            >
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                disabled={activeStep >= 3 && 
                                    !['0', '1', '2', '3'].every(step => location.state?.filledSteps?.includes(step))}
                                onClick={() => {
                                    handleNext(values, {
                                        setFieldError,
                                        validateForm,
                                        setSubmitting: () => { },
                                        resetForm: () => { }
                                    });
                                }}
                            >
                                {isLastStep ? (liasoningData?._id ? 'Update' : 'Submit') : 'Next'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                            >
                                Save
                            </Button>
                        </Stack>
                    </FormControl>
                    <Dialog
                        fullScreen={fullScreen}
                        open={openView}
                        onClose={handleCloseView}
                        aria-labelledby="responsive-dialog-title"
                        maxWidth={"sm"}
                        fullWidth
                    >
                        <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: 'right' }}>
                            <Button
                                onClick={handleDownload}
                                style={{ color: theme.palette.text.primary }}
                                startIcon={<DownloadIcon />}
                                disabled={!viewImage}
                            >
                                Download
                            </Button>
                        </DialogTitle>
                        <DialogContent style={{ backgroundColor: theme.palette.background.purple, display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                            {viewImage ? (
                                viewImage instanceof File ? (
                                    viewImage.name.toLowerCase().endsWith('.pdf') ? (
                                        <iframe
                                            src={URL.createObjectURL(viewImage)}
                                            style={{ width: '100%', height: '80vh', border: 'none' }}
                                            title="PDF Preview"
                                        />
                                    ) : (
                                        <img
                                            src={URL.createObjectURL(viewImage)}
                                            alt="Uploaded Image"
                                            style={{ width: '100%', height: '80vh', objectFit: 'contain' }}
                                            ref={downloadRef}
                                        />
                                    )
                                ) : (
                                    typeof viewImage === 'string' && viewImage.toLowerCase().endsWith('.pdf') ? (
                                        <iframe
                                            src={viewImage}
                                            style={{ width: '100%', height: '80vh', border: 'none' }}
                                            title="PDF Preview"
                                        />
                                    ) : (
                                        <img
                                            src={viewImage}
                                            alt="Uploaded Image"
                                            style={{ width: '100%', height: '80vh', objectFit: 'contain' }}
                                            ref={downloadRef}
                                        />
                                    )
                                )
                            ) : (
                                <p>No image to display</p>
                            )}
                        </DialogContent>
                        <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                            <Button autoFocus onClick={handleCloseView} style={{ color: theme.palette.text.primary }}>
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Stack>
            </form>
        </Box >
    );
}   